import { useEffect, useRef, useState } from "react";
import { assetFor } from "../render/assets";
import { SCENE_HEIGHT, SCENE_WIDTH } from "../render/draw";
import { drawHatchScene, hatchFrameAt, stillCaption } from "../render/hatch";
import type { PreviousPetView } from "./view-model";

export interface HatchSequenceProps {
  previous: PreviousPetView;
  species: string;
  reducedMotion: boolean;
  /** Called once when the sequence ends or the player skips it. Must be stable between renders. */
  onDone(): void;
}

/**
 * The Monday hatch sequence in the tank (docs/handoff/006-shell-reference.html `renderAnim`): the old pet goes into the book
 * or rests, then the egg hatches. With reduced motion it shows the still end state and waits for "Continue".
 */
export function HatchSequence({ previous: latest, species, reducedMotion, onDone }: HatchSequenceProps) {
  // The view makes a new `previous` object on every refresh; the sequence keeps the first one so it never restarts.
  const [previous] = useState(latest);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [caption, setCaption] = useState(() => (reducedMotion ? stillCaption(previous) : hatchFrameAt(0, previous).caption));

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas && typeof canvas.getContext === "function" ? canvas.getContext("2d") : null;
    if (!ctx) return;
    const newAsset = assetFor(species);
    const asset = assetFor(previous.species);
    if (reducedMotion) {
      drawHatchScene(ctx, asset, previous, null, 0, true, newAsset);
      return;
    }
    const start = performance.now();
    let raf = 0;
    const step = (now: number) => {
      const elapsed = now - start;
      const frame = hatchFrameAt(elapsed, previous);
      drawHatchScene(ctx, asset, previous, frame, Math.floor(elapsed / 350), false, newAsset);
      setCaption(frame.caption);
      if (frame.done) {
        onDone();
        return;
      }
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [previous, species, reducedMotion, onDone]);

  return (
    <>
      <canvas
        ref={canvasRef}
        className="pet-tank-canvas"
        width={SCENE_WIDTH}
        height={SCENE_HEIGHT}
        role="img"
        aria-label={`The Monday hatch: ${previous.name} and a new egg`}
        data-testid="hatch-canvas"
      />
      <div className="animcap" data-testid="hatch-caption">
        <span role="status" aria-live="polite">
          {caption}
        </span>
        <button className="btn" type="button" data-testid="hatch-skip" onClick={onDone}>
          {reducedMotion ? "Continue" : "Skip"}
        </button>
      </div>
    </>
  );
}
